import * as Yup from 'yup';

import { showAlert } from 'components/shared/Alert/Alert';

import { BILLING_ADDRESS_FIELDS } from './constants';

export const PRE_AUTH_FIELD_LABELS = {
  cardId: 'Payment method',
  cardholderName: 'Cardholder name',
  address_ln_1: 'Address',
  city: 'City',
  state: 'State',
  zip: 'Zip code',
  signature: 'Cardholder signature',
  authorized: 'Authorization'
};

const REQUIRED_BILLING_ADDRESS_FIELDS = ['address_ln_1', 'city', 'state', 'zip'];

const hasValue = (value) => !!String(value ?? '').trim();

export const PreAuthModalValidationSchema = Yup.object()
  .shape({
    cardId: Yup.mixed().when('isNewCard', {
      is: false,
      then: (schema) => schema.required('Please select a card or add a new one'),
      otherwise: (schema) => schema.nullable()
    }),
    cardholderName: Yup.string().when(['processor', 'cardId'], {
      is: (processor, cardId) => processor === 'payroc' || !!cardId,
      then: (schema) => schema,
      otherwise: (schema) =>
        schema
          .required('Cardholder name is required')
          .test(
            'has-full-name',
            'Cardholder name is required. Please enter both first and last name',
            (value) => {
              if (!value) return false;
              const trimmed = value.trim();
              const parts = trimmed.split(/\s+/);
              return parts.length >= 2 && parts.every((part) => part.length > 0);
            }
          )
    }),
    signature: Yup.string().required('Signature is required'),
    address_ln_1: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zip: Yup.string(),
    // The form copy reads "By signing below, you authorize the above", so the signature is the
    // authorization; the flag only has to stand on its own when nothing was signed.
    authorized: Yup.boolean().when('signature', {
      is: (signature) => !!signature,
      then: (schema) => schema,
      otherwise: (schema) => schema.oneOf([true], 'You must authorize the payment')
    })
  })
  .test('billing-address-complete', '', (values, ctx) => {
    if (values?.cardId) return true;

    if (!BILLING_ADDRESS_FIELDS.some((field) => hasValue(values?.[field]))) return true;

    const missing = REQUIRED_BILLING_ADDRESS_FIELDS.find((field) => !hasValue(values?.[field]));
    if (!missing) return true;

    return ctx.createError({
      path: missing,
      message: `${PRE_AUTH_FIELD_LABELS[missing]} is required to save a billing address. Clear the other billing fields to continue without one.`
    });
  });

const FIELD_ORDER = Object.keys(PRE_AUTH_FIELD_LABELS);

const findFieldNode = (field) => {
  if (typeof document === 'undefined') return null;
  const nodes = Array.from(
    document.querySelectorAll(`[data-preauth-field="${field}"], [name="${field}"]`)
  );
  return nodes.find((node) => node.offsetParent !== null) || nodes[0] || null;
};

export const validatePreAuthModal = async (formik) => {
  const errors = await formik.validateForm();
  if (!errors || !Object.keys(errors).length) return true;

  formik.setTouched(
    Object.keys(errors).reduce((touched, field) => {
      touched[field] = true;
      return touched;
    }, {})
  );

  const field = FIELD_ORDER.find((name) => errors[name]) || Object.keys(errors)[0];
  const node = findFieldNode(field) || findFieldNode('signature');
  node?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  showAlert({
    title: `${PRE_AUTH_FIELD_LABELS[field] || 'Required information'} is required`,
    message: errors[field],
    color: 'danger'
  });

  return false;
};
